import React, { useState } from 'react';
import { Mail, Lock, Loader2, EyeOff, Eye } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { LoginFormData, AuthError } from '../types/auth';

interface LoginFormProps {
  formData: LoginFormData;
  isLoading: boolean;
  error: AuthError | null;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
  otpSent?: boolean;
  otp?: string;
  setOtp?: (otp: string) => void;
  otpVerified?: boolean;
  isVerifying?: boolean;
  timer?: number;
  canResend?: boolean;
  onResendOtp?: () => Promise<void>;
  onVerifyOtp?: () => Promise<void>;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  formData,
  isLoading,
  error,
  onInputChange,
  onSubmit,
  otpSent = false,
  otp = "",
  setOtp,
  otpVerified = false,
  isVerifying = false,
  timer = 0,
  canResend = true,
  onResendOtp,
  onVerifyOtp
}) => {
  const [showPassword, setShowPassword] = useState(false);
  
  return (
    <form className="space-y-6" onSubmit={onSubmit}>
      {!otpSent && (
        <>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email address
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="pl-10"
                placeholder="Enter your email"
                value={formData.email}
                onChange={onInputChange}
                disabled={otpSent}
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                className={`pl-9 pr-20  [&::-ms-reveal]:hidden [&::-ms-clear]:hidden [&::-webkit-credentials-auto-fill-button]:hidden [&::-webkit-credentials-auto-fill-button]:hover:bg-none [&::-webkit-credentials-auto-fill-button]:active:bg-none [&::-webkit-credentials-auto-fill-button]:focus:bg-none`}
                placeholder="Enter your password"
                value={formData.password}
                onChange={onInputChange}
                disabled={otpSent}
              />
              <button
                type="button" 
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <div className="flex items-center">
            <input
              id="remember-me"
              name="rememberMe"
              type="checkbox"
              className="h-4 w-4 text-[#EB1C37] focus:ring-[#EB1C37] border-gray-300 rounded"
              checked={formData.rememberMe}
              onChange={onInputChange}
              disabled={otpSent}
            />
            <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
              Remember me
            </label>
          </div>
        </>
      )}

      {error && (
        <div className="text-sm text-readable-red bg-[rgba(196,0,31,0.06)] rounded-md p-3">
          {error.message}
        </div>
      )}

      {!otpSent && (
        <div>
          <Button
            type="submit"
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#EB1C37] hover:bg-[#EB1C37]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#EB1C37] disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                Validating...
              </>
            ) : (
              'Login'
            )}
          </Button>
        </div>
      )}

      {/* OTP section appears after OTP is sent */}
      {otpSent && !otpVerified && (
        <div className="space-y-4">
          <div>
            <label
              htmlFor="otp"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Enter OTP
            </label>
            <p className="text-sm text-gray-600 mb-4">
              We've sent a 6-digit OTP to {formData.email}
            </p>

            {/* OTP input boxes */}
            <div className="flex justify-between gap-2">
              {Array.from({ length: 6 }).map((_, index) => (
                <Input
                  key={index}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className={`w-12 h-12 text-center text-lg border rounded-md focus:outline-none ${
                    otp.length === 6
                      ? ""
                      : "border-gray-300 focus:border-[#EB1C37]"
                  }`}
                  value={otp[index] || ""}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/, "");
                    const newOtp = otp.split("");
                    newOtp[index] = value;
                    if (setOtp) {
                      setOtp(newOtp.join(""));
                    }
                    if (value && index < 5) {
                      const next = document.getElementById(
                        `otp-${index + 1}`
                      );
                      next && next.focus();
                    }
                  }}
                  onPaste={
                    index === 0
                      ? (e) => {
                          e.preventDefault();
                          const pastedText =
                            e.clipboardData.getData("text");
                          const digits = pastedText.replace(/\D/g, "");
                          const digitsToUse = digits.slice(0, 6);
                          if (setOtp) {
                            setOtp(digitsToUse);
                          }
                          // Focus the last filled input or next empty input
                          const focusIndex = Math.min(
                            digitsToUse.length - 1,
                            5
                          );
                          setTimeout(() => {
                            const targetInput = document.getElementById(
                              `otp-${focusIndex}`
                            );
                            targetInput && targetInput.focus();
                          }, 0);
                        }
                      : undefined
                  }
                  onKeyDown={(e) => {
                    if (
                      e.key === "Backspace" &&
                      !otp[index] &&
                      index > 0
                    ) {
                      const prev = document.getElementById(
                        `otp-${index - 1}`
                      );
                      prev && prev.focus();
                    }
                  }}
                  id={`otp-${index}`}
                />
              ))}
            </div>
          </div>

          {/* Verify OTP Button */}
          <Button
            type="button"
            className="w-full bg-[#EB1C37] text-white py-2"
            onClick={onVerifyOtp}
            disabled={isVerifying || otp.length < 6}
          >
            {isVerifying ? (
              <>
                <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                Verifying...
              </>
            ) : (
              "Verify OTP & Sign In"
            )}
          </Button>

          {/* Resend OTP Section */}
          <div className="text-center text-sm text-gray-600">
            <button
              type="button"
              onClick={async () => {
                // Reset OTP input boxes
                if (setOtp) {
                  setOtp("");
                }
                // Focus the first OTP input after reset
                setTimeout(() => {
                  const firstInput = document.getElementById("otp-0");
                  firstInput && firstInput.focus();
                }, 0);
                // Call the parent resend handler
                if (onResendOtp) {
                  await onResendOtp();
                }
              }}
              disabled={!canResend}
              className={`font-medium ${
                canResend
                  ? "text-[#EB1C37] hover:underline"
                  : "text-gray-400 cursor-not-allowed"
              }`}
            >
              {canResend ? "Resend OTP" : `Resend OTP in ${timer}s`}
            </button>
          </div>
        </div>
      )}

      {otpVerified && (
        <div className="text-center">
          <div className="text-sm text-green-600 bg-green-50 rounded-md p-3">
            OTP verified successfully. Signing in...
          </div>
        </div>
      )}
    </form>
  );
};